import { isProductionHost } from './environment';

/**
 * OneTrust consent gate for analytics.
 *
 * OneTrust is not part of this codebase. It is injected by Tealium
 * (tags.tiqcdn.com/utag/davidsbridal/main-pearl/{dev|prod}/utag.js) via a tag
 * whose load rule currently only matches production hosts. So "no CMP present"
 * is a normal, expected state here and is deliberately kept distinct from
 * "consent denied" — conflating the two would either silently disable
 * analytics everywhere or silently ignore a real opt-out.
 *
 * Category IDs are OneTrust's defaults, confirmed against the live production
 * banner (C0001 Strictly Necessary, C0002 Performance, C0003 Functional,
 * C0004 Targeting). PostHog is analytics, so it is gated on C0002.
 */

const PERFORMANCE_GROUP = 'C0002';

/**
 * OneTrust's active group list, e.g. ',C0001,C0002,'.
 *
 * Returns null when OneTrust has not loaded or has not yet produced a state,
 * so callers can tell "never asked" from "declined". A necessary-only opt-out
 * still returns a real string (it contains C0001), so declines are honored
 * rather than mistaken for an unreadable CMP.
 */
const readActiveGroups = () => {
    if (typeof window === 'undefined') return null;
    const groups = window.OnetrustActiveGroups;
    if (typeof groups !== 'string' || groups.replace(/,/g, '').trim() === '') {
        return null;
    }
    return groups;
};

const isGroupActive = (groups, groupId) =>
    groups
        .split(',')
        .map((id) => id.trim())
        .includes(groupId);

/**
 * Whether analytics may capture right now.
 *
 * With a readable CMP this is simply "is C0002 granted". With no CMP present
 * it falls back to the host rule in lib/environment.js: allowed on known
 * non-production hosts so local dev and QA keep working, denied everywhere
 * else — including on any hostname not recognized as non-production.
 */
const hasAnalyticsConsent = () => {
    const groups = readActiveGroups();
    if (groups === null) return !isProductionHost();
    return isGroupActive(groups, PERFORMANCE_GROUP);
};

let consentChangeRegistered = false;

/**
 * Run `callback` whenever the visitor's consent state may have changed.
 *
 * Two hooks are required and neither is sufficient alone:
 *   - OptanonWrapper is invoked by OneTrust once it has loaded and again on
 *     every save. It is the only signal for a returning visitor whose choice
 *     is already stored, and it is how we learn OneTrust exists at all.
 *   - OnConsentChanged fires only on *subsequent* changes, and only exists
 *     once the SDK object is present — so it is registered from inside the
 *     wrapper rather than at subscribe time.
 *
 * OptanonWrapper is chained rather than replaced: it is undefined today, but
 * Tealium's OneTrust template may start defining it, and clobbering theirs
 * would break whatever they hang off it.
 *
 * @param {() => void} callback
 * @returns {() => void} unsubscribe
 */
const onConsentChange = (callback) => {
    if (typeof window === 'undefined') return () => {};

    let active = true;
    const safeCallback = () => {
        if (active) callback();
    };

    const registerSdkListener = () => {
        if (consentChangeRegistered) return;
        const oneTrust = window.OneTrust;
        if (!oneTrust || typeof oneTrust.OnConsentChanged !== 'function') return;
        try {
            oneTrust.OnConsentChanged(safeCallback);
            consentChangeRegistered = true;
        } catch {
            // A CMP that can't be subscribed to must not break the app; the
            // host rule and the wrapper hook still apply.
        }
    };

    const previousWrapper = window.OptanonWrapper;
    window.OptanonWrapper = function OptanonWrapper(...args) {
        if (typeof previousWrapper === 'function') {
            try {
                previousWrapper.apply(this, args);
            } catch {
                // Not ours to handle — never let it stop our own sync.
            }
        }
        registerSdkListener();
        safeCallback();
    };

    // OneTrust may already have loaded and called OptanonWrapper before we got
    // here, in which case it will not call it again.
    registerSdkListener();

    return () => {
        active = false;
    };
};

export { PERFORMANCE_GROUP, hasAnalyticsConsent, onConsentChange, readActiveGroups };
